import { createContext, useContext } from 'react';
import { useQueryStringState } from './use-query-string-state';

/**
 * Shape of search params source (setters always; params/previousParams optional for setters-only mode).
 * When a provider supplies this, advance-table and related hooks use it instead of URL.
 * Used by elephant search flow to read/write from the search params store (store-first).
 */
export interface SearchParamsSourceValue<
  T extends Record<string, string> = Record<string, string>
> {
  params?: Partial<T>;
  previousParams?: Partial<T>;
  setParams: (
    values?: Partial<T>,
    skipHistory?: boolean,
    undoredo?: boolean
  ) => void;
  setParam: (
    key: keyof T | string,
    value?: string,
    skipHistory?: boolean
  ) => void;
  removeParam: (key: keyof T | string, skipHistory?: boolean) => void;
}

/** Params-only context; when present with setters-only source, useSearchParamsSource combines them. */
export interface SearchParamsParamsValue<
  T extends Record<string, string> = Record<string, string>
> {
  params: Partial<T>;
  previousParams?: Partial<T>;
}

const SearchParamsSourceContext = createContext<SearchParamsSourceValue | null>(
  null
);

const SearchParamsParamsContext = createContext<SearchParamsParamsValue | null>(
  null
);

const { Provider: SearchParamsSourceContextProvider } =
  SearchParamsSourceContext;
const { Provider: SearchParamsParamsContextProvider } =
  SearchParamsParamsContext;

export {
  SearchParamsSourceContext,
  SearchParamsSourceContextProvider,
  SearchParamsParamsContext,
  SearchParamsParamsContextProvider,
};

/**
 * Returns the current search params source.
 * If a SearchParamsSourceContextProvider supplies a value (e.g. elephant search store),
 * that is used. When source is setters-only (no params) and SearchParamsParamsContext
 * is present, params come from the params context so only the table subtree re-renders.
 * When source has no params and no params context, falls back to useQueryStringState() (URL-based).
 */
export function useSearchParamsSource<
  T extends Record<string, string> & {
    page?: string | number;
    size?: string | number;
  } = Record<string, string>
>(config?: { watch?: Array<keyof T>; debounce?: number }) {
  const sourceContext = useContext(SearchParamsSourceContext);
  const paramsContext = useContext(SearchParamsParamsContext);
  const urlState = useQueryStringState<T>(config);

  if (sourceContext != null) {
    const hasParamsInSource =
      sourceContext.params !== undefined && sourceContext.params !== null;
    if (hasParamsInSource) {
      return sourceContext as SearchParamsSourceValue<T>;
    }
    if (paramsContext != null) {
      return {
        ...sourceContext,
        params: paramsContext.params as Partial<T>,
        previousParams: paramsContext.previousParams as Partial<T> | undefined,
      } as SearchParamsSourceValue<T>;
    }
    return urlState;
  }
  return urlState;
}
